import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Avatar,
    Typography,
    Button,
    CircularProgress,
    Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import HamburgerMenu from "./HamburgerMenu";

// ── Constants ──
const SIDEBAR_WIDTH_EXPANDED = 200;
const SIDEBAR_WIDTH_COLLAPSED = 60;

// ── Styled components ──
const SidebarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isOpen",
})(({ isOpen }) => ({
    width: isOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e8ecf1",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 10,
    transition: "width 0.3s ease-in-out",
}));

const StyledListItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "active" && prop !== "isOpen",
})(({ active, isOpen }) => ({
    padding: isOpen ? "8px 24px" : "8px 18px",
    borderLeft: active ? "3px solid #2bbcc0" : "3px solid transparent",
    backgroundColor: "transparent",
    justifyContent: isOpen ? "flex-start" : "center",
    "&:hover": {
        backgroundColor: "transparent",
        "& .MuiListItemIcon-root": {
            color: "#2bbcc0",
        },
        "& .MuiListItemText-primary": {
            color: "#2bbcc0",
        },
    },
    "& .MuiListItemIcon-root": {
        minWidth: isOpen ? 36 : "auto",
        color: active ? "#2bbcc0" : "#718096",
    },
    "& .MuiListItemText-primary": {
        fontSize: 14,
        fontWeight: 500,
        color: active ? "#2bbcc0" : "#718096",
    },
}));

// // ── Logo component ──
// const NetgainLogo = () => (
//     <Box sx={{ display: "flex", alignItems: "center", px: 3, py: 2.5 }}>
//       <img
//           src="https://assets-global.website-files.com/65ec2369cf88488dc229a024/65ec8d5c65acaa4acbe020d2_netgain-logo.svg"
//           alt="NetGain Logo"
//           style={{ height: 32 }}
//       />
//     </Box>
// );

// ── Sidebar component ──
const Sidebar = ({
    customerListAccess,
    activeImplementationsAccess,
    metricsAccess,
    accessCheckComplete,
    selectedTab,
    onTabChange,
    isOpen = true,
    onToggle,
    sx,
    user,
    userLoading,
    onLogout,
}) => {
    // Show all items only after access check completes
    const navItems = accessCheckComplete
        ? [
              // Only one "Customers" item displays at a time based on customerListAccess:
              // - With access: shows full customer list (index 1)
              // - Without access: shows search-only view (index 0)
              {
                  label: "Customers",
                  icon: <SearchOutlinedIcon />,
                  index: 0,
                  visible: !customerListAccess,
              },
              {
                  label: "Customers",
                  icon: <PeopleOutlinedIcon />,
                  index: 1,
                  visible: customerListAccess,
              },
              {
                  label: "Deal Summary",
                  icon: <SummarizeOutlinedIcon />,
                  index: 2,
                  visible: true,
              },
              {
                  label: "Implementations",
                  icon: <BuildOutlinedIcon />,
                  index: 3,
                  visible: activeImplementationsAccess,
              },
              {
                  label: "Support",
                  icon: <SupportAgentOutlinedIcon />,
                  index: 4,
                  visible: activeImplementationsAccess,
              },
              {
                  label: "Metrics",
                  icon: <BarChartOutlinedIcon />,
                  index: 5,
                  visible: metricsAccess,
              },
              {
                  label: "Reports",
                  icon: <AssessmentOutlinedIcon />,
                  index: 6,
                  visible: metricsAccess,
              },
              {
                  label: "Product Health",
                  icon: <MonitorHeartOutlinedIcon />,
                  index: 7,
                  visible: metricsAccess,
              },
              { label: "Matrixed Org", icon: <GroupsOutlinedIcon />, index: 8, visible: true },
          ].filter((item) => item.visible)
        : [];

    return (
        <SidebarContainer isOpen={isOpen} sx={sx}>
            <Fade in={accessCheckComplete} timeout={500}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        px: 2,
                        pt: 1.5,
                        pb: 1.5,
                    }}
                >
                    <HamburgerMenu onClick={onToggle} isOpen={isOpen} />
                </Box>
            </Fade>

            <Fade in={accessCheckComplete} timeout={500}>
                <List disablePadding>
                    {navItems.map((item) => (
                        <Tooltip
                            key={item.index}
                            title={item.label}
                            placement="right"
                            disableHoverListener={isOpen}
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "#2d3748",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        px: 1.5,
                                        py: 0.75,
                                        "& .MuiTooltip-arrow": {
                                            color: "#2d3748",
                                        },
                                    },
                                },
                            }}
                        >
                            <StyledListItemButton
                                active={selectedTab === item.index}
                                isOpen={isOpen}
                                onClick={() => onTabChange && onTabChange(item.index)}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                {isOpen && <ListItemText primary={item.label} />}
                            </StyledListItemButton>
                        </Tooltip>
                    ))}
                </List>
            </Fade>

            {/* User section at bottom */}
            <Box sx={{ marginTop: "auto", borderTop: "1px solid #e8ecf1", p: isOpen ? 2 : 1 }}>
                {userLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={24} sx={{ color: "#718096" }} />
                    </Box>
                ) : user ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: isOpen ? "flex-start" : "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: isOpen ? 1.5 : 0,
                            }}
                        >
                            <Tooltip
                                title={user.email || user.name}
                                placement="right"
                                disableHoverListener={isOpen}
                            >
                                <Avatar
                                    sx={{ width: 32, height: 32, bgcolor: "#2bbcc0", fontSize: 14 }}
                                >
                                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                                </Avatar>
                            </Tooltip>
                            {isOpen && (
                                <Box sx={{ overflow: "hidden" }}>
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: "#2d3748",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {user.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: "#718096",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Tooltip title="Login" placement="right" disableHoverListener={isOpen}>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<LoginOutlinedIcon />}
                            href="/api/auth/sso/login"
                            sx={{
                                color: "#718096",
                                fontSize: 12,
                                textTransform: "none",
                                justifyContent: isOpen ? "flex-start" : "center",
                                minWidth: isOpen ? "auto" : 40,
                                "&:hover": { color: "#2bbcc0", bgcolor: "transparent" },
                            }}
                        >
                            {isOpen && "Login"}
                        </Button>
                    </Tooltip>
                )}
            </Box>
        </SidebarContainer>
    );
};

export { Sidebar };
